/**
 * Ecranul zilei si editarea inline — livrabilul principal.
 *
 * Regula de comportament din plan, in trei propozitii:
 *
 *   1. **Editarea celulei e instantanee.** Valoarea apare in grila inainte sa plece cererea. O
 *      celula de suma care asteapta 300 ms un round-trip inainte sa arate ce ai tastat te invata sa
 *      nu ai incredere in ea, iar in Excel nu asteptai niciodata.
 *   2. **La esec valoarea RĂMÂNE.** Rollbackul readuce in cache datele SERVERULUI (altfel totalul
 *      zilei ar minti), dar textul tastat se muta in `magazinEditari` si se afiseaza mai departe cu
 *      chip `Nesalvat`. Un instrument care sterge ce ai scris pentru ca a picat VPN-ul pierde
 *      increderea o data si definitiv.
 *   3. **409 nu e o eroare, e un protocol.** Cineva a scris intre timp; se reciteste randul, se
 *      arata valoarea tastata langa cea noua a serverului si omul decide daca reaplica. O
 *      reincercare oarba cu aceeasi `version` da acelasi 409 la infinit.
 *
 * Starea per celula (`modificat → salvez → salvat`) se citeste din `useMutationState`, nu dintr-un
 * `useState` local: mutatia supravietuieste demontarii celulei (scroll virtualizat, coada offline),
 * deci sursa de adevar trebuie sa fie aceeasi in ambele cazuri.
 */

import { useCallback, useMemo } from 'react';
import {
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { effectiveStatus, type StatusCode } from '@evomag/core/status';

import { api, type CuImplicite } from '../lib/api.ts';
import { esteConflict } from '../lib/api-errors.ts';
import { chei } from '../lib/query-keys.ts';
import {
  derivaStareCelula,
  magazinEditari,
  useEditareLocala,
  type StareSalvare,
} from './editari.ts';
import type {
  BulkEntriesInput,
  CreateEntryInput,
  CreateRunInput,
  DayEntryDto,
  DayRunDto,
  DayWorkspaceResponse,
  PatchEntryInput,
} from '../../../../functions/api/schemas.ts';

export type { DayEntryDto, DayRunDto, DayWorkspaceResponse };

// ---------------------------------------------------------------------------
// GET /api/zi/:data
// ---------------------------------------------------------------------------

export function useZi(
  data: string,
  optiuni?: { runda?: string; activ?: boolean },
): UseQueryResult<DayWorkspaceResponse, Error> {
  return useQuery({
    // `runda` NU intra in cheie: e un FILTRU de client peste acelasi raspuns (ruta intoarce toate
    // rundele zilei). O cheie per runda ar cere aceeasi zi de N ori de la server.
    queryKey: chei.zi.data(data),
    queryFn: ({ signal }) => api.get<DayWorkspaceResponse>(`/zi/${data}`, { semnal: signal }),
    enabled: optiuni?.activ !== false && data !== '',
  });
}

/** Filtrarea pe runda se face aici, ca sa nu se duplice in fiecare componenta. */
export function intrariDinRunda(
  zi: DayWorkspaceResponse | undefined,
  runda?: string,
): readonly DayEntryDto[] {
  if (!zi) return [];
  if (!runda) return zi.entries;
  const potrivite = zi.runs.filter(
    (r) => r.labelRaw === runda || r.operatorLabel === runda || String(r.seq) === runda,
  );
  const idRunde = new Set(potrivite.map((r) => r.runId));
  return zi.entries.filter((e) => idRunde.has(e.runId));
}

// ---------------------------------------------------------------------------
// PATCH /api/entries/:id
// ---------------------------------------------------------------------------

/** Campurile editabile inline. Numele sunt exact cele din `PatchEntryInput`. */
export type CampIntrare = keyof PatchEntryInput;

/** Campurile care sunt metadate de scriere, nu valori de celula. Nu produc marcaj `Nesalvat`. */
const CAMPURI_TEHNICE: ReadonlySet<string> = new Set(['version', 'appVersion', 'reason']);

export interface VariabilePatchIntrare {
  entryId: number;
  patch: PatchEntryInput;
  /**
   * Valorile deja convertite de client. `parseAmountInput` din `@evomag/core` e SINGURUL loc unde
   * se rotunjeste o suma, si el ruleaza in componenta de celula. Cand lipsesc, hookul aplica
   * optimist doar campurile scalare si asteapta serverul pentru cifrele derivate: clientul nu
   * inventeaza o valoare in lei.
   */
  optimist?: Partial<DayEntryDto>;
  /** Textul brut per camp, pastrat ca sa poata fi reafisat daca scrierea eșuează. */
  brut?: Partial<Record<CampIntrare, string>>;
}

export interface RaspunsPatchIntrare {
  entry: DayEntryDto;
  run: { runId: number; dePlataMinor: number | null };
}

export function fnPatchIntrare(variabile: VariabilePatchIntrare): Promise<RaspunsPatchIntrare> {
  return api.patch<RaspunsPatchIntrare>(`/entries/${variabile.entryId}`, variabile.patch);
}

/**
 * Aplica optimist ce se poate deduce FARA aritmetica de bani.
 *
 * Statusul efectiv se recalculeaza cu aceeasi functie ca serverul (`effectiveStatus` din
 * `@evomag/core`), deci nu exista sansa ca grila sa arate alta stare decat cea care se va salva.
 */
export function aplicaOptimist(intrare: DayEntryDto, variabile: VariabilePatchIntrare): DayEntryDto {
  const { patch, optimist } = variabile;
  const urmatoare: DayEntryDto = { ...intrare };

  if (patch.statusOverride !== undefined) {
    urmatoare.statusOverride = patch.statusOverride;
    urmatoare.status = effectiveStatus(
      intrare.statusImported as StatusCode,
      patch.statusOverride as StatusCode | null,
    );
  }
  if (patch.dueDate !== undefined) urmatoare.dueDate = patch.dueDate;
  if (patch.currency !== undefined) urmatoare.currency = patch.currency;
  if (patch.blockedReason !== undefined) urmatoare.blockedReason = patch.blockedReason;
  if (patch.needsAttention !== undefined) urmatoare.needsAttention = patch.needsAttention;
  if (patch.attentionReason !== undefined) urmatoare.attentionReason = patch.attentionReason;
  if (patch.paidFromAccountId !== undefined) urmatoare.paidFromAccountId = patch.paidFromAccountId;
  if (patch.postponePolicy !== undefined) urmatoare.postponePolicy = patch.postponePolicy;
  if (patch.postponeToDate !== undefined) urmatoare.postponeToDate = patch.postponeToDate;
  if (patch.rateE6 !== undefined) urmatoare.manualRateE6 = patch.rateE6;

  return optimist ? { ...urmatoare, ...optimist } : urmatoare;
}

function inlocuiesteIntrare(
  zi: DayWorkspaceResponse,
  entryId: number,
  transforma: (intrare: DayEntryDto) => DayEntryDto,
): DayWorkspaceResponse {
  return { ...zi, entries: zi.entries.map((e) => (e.id === entryId ? transforma(e) : e)) };
}

/** Campurile de VALOARE dintr-un patch (fara `version`, `appVersion`, `reason`). */
export function campuriDinPatch(patch: PatchEntryInput): readonly CampIntrare[] {
  return Object.keys(patch).filter((k) => !CAMPURI_TEHNICE.has(k)) as CampIntrare[];
}

export interface ContextPatchIntrare {
  anterior: DayWorkspaceResponse | undefined;
  campuri: readonly CampIntrare[];
}

export function usePatchIntrare(
  data: string,
): UseMutationResult<RaspunsPatchIntrare, Error, VariabilePatchIntrare, ContextPatchIntrare> {
  const queryClient = useQueryClient();
  return useMutation(optiuniPatchIntrare(queryClient, data));
}

/**
 * Optiunile mutatiei, separate de hook.
 *
 * Doua motive: se pot testa cu un `QueryClient` gol, fara sa randam nimic, si se pot inregistra ca
 * `mutationDefaults` pentru coada offline — o mutatie restaurata dupa reload nu are componenta care
 * a creat-o, deci trebuie sa-si regaseasca `mutationFn`-ul dupa cheie.
 */
export function optiuniPatchIntrare(queryClient: QueryClient, data: string) {
  return {
    mutationKey: chei.mutatii.patchIntrare(),
    mutationFn: fnPatchIntrare,

    onMutate: async (variabile: VariabilePatchIntrare): Promise<ContextPatchIntrare> => {
      // Fara `cancelQueries`, un refetch pornit inainte de editare ar ateriza DUPA scrierea
      // optimista si ar sterge-o — exact bugul „valoarea a clipit si a disparut".
      await queryClient.cancelQueries({ queryKey: chei.zi.data(data) });
      const anterior = queryClient.getQueryData<DayWorkspaceResponse>(chei.zi.data(data));
      const campuri = campuriDinPatch(variabile.patch);

      // Campul intra „in zbor": cat timp mutatia e `pending`, celula arata `salvez`, nu `modificat`.
      magazinEditari.curata('intrare', variabile.entryId, campuri);

      if (anterior) {
        queryClient.setQueryData<DayWorkspaceResponse>(
          chei.zi.data(data),
          inlocuiesteIntrare(anterior, variabile.entryId, (intrare) =>
            aplicaOptimist(intrare, variabile),
          ),
        );
      }

      return { anterior, campuri };
    },

    onError: (
      eroare: Error,
      variabile: VariabilePatchIntrare,
      context: ContextPatchIntrare | undefined,
    ): void => {
      // Cache-ul revine la ADEVARUL SERVERULUI: totalul zilei nu are voie sa contina o suma care
      // nu s-a scris nicaieri.
      if (context?.anterior) queryClient.setQueryData(chei.zi.data(data), context.anterior);

      // ...dar textul tastat NU se pierde: se muta langa celula, cu `Nesalvat` / `Reîncearcă`.
      const conflict = esteConflict(eroare);
      const acum = Date.now();
      for (const camp of context?.campuri ?? []) {
        const brut = variabile.brut?.[camp];
        if (brut === undefined) continue;
        magazinEditari.marcheaza({
          zona: 'intrare',
          id: variabile.entryId,
          camp,
          valoare: brut,
          stare: conflict ? 'conflict' : 'nesalvat',
          mesaj: eroare.message,
          la: acum,
        });
      }

      // La 409 apelantul are nevoie de versiunea NOUA ca sa poata reaplica peste ea.
      if (conflict) void queryClient.invalidateQueries({ queryKey: chei.zi.data(data) });
    },

    onSuccess: (
      raspuns: RaspunsPatchIntrare,
      variabile: VariabilePatchIntrare,
      context: ContextPatchIntrare | undefined,
    ): void => {
      magazinEditari.curata('intrare', variabile.entryId, context?.campuri ?? []);

      // Randul autoritativ intra imediat in cache, ca celula sa nu clipeasca inapoi pe valoarea
      // optimista in intervalul dintre raspuns si refetch.
      const curent = queryClient.getQueryData<DayWorkspaceResponse>(chei.zi.data(data));
      if (curent) {
        queryClient.setQueryData<DayWorkspaceResponse>(chei.zi.data(data), {
          ...inlocuiesteIntrare(curent, variabile.entryId, () => raspuns.entry),
          runs: curent.runs.map((r) =>
            r.runId === raspuns.run.runId ? { ...r, dePlataMinor: raspuns.run.dePlataMinor } : r,
          ),
        });
      }

      // Refetchul aduce KPI-ul zilei (`Disponibil după plată`), care nu vine in raspunsul de PATCH.
      void invalideazaDupaScriereZi(queryClient, data);
    },
  };
}

/**
 * Ce se invalideaza dupa orice scriere pe o zi.
 *
 * Matricea si cashflowul citesc ACELEASI randuri prin alte view-uri, deci o suma schimbata in
 * ecranul zilei le face pe amandoua invechite. Se invalideaza PREFIXUL, nu o cheie exacta:
 * intervalele deschise in alte taburi nu sunt cunoscute aici.
 */
export async function invalideazaDupaScriereZi(
  queryClient: QueryClient,
  data: string,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: chei.zi.data(data) }),
    queryClient.invalidateQueries({ queryKey: chei.matrice.tot() }),
    queryClient.invalidateQueries({ queryKey: chei.cashflow.tot() }),
  ]);
}

// ---------------------------------------------------------------------------
// Starea de salvare per celula
// ---------------------------------------------------------------------------

/** Starea de salvare a UNEI celule. Filtreaza mutatiile care chiar ating campul cerut. */
export function useStareCelula(entryId: number, camp: CampIntrare): StareSalvare {
  const stari = useMutationState({
    filters: { mutationKey: chei.mutatii.patchIntrare(entryId), exact: false },
    select: (mutatie) => ({
      status: mutatie.state.status,
      submittedAt: mutatie.state.submittedAt,
      variabile: mutatie.state.variables as VariabilePatchIntrare | undefined,
    }),
  });

  const editare = useEditareLocala('intrare', entryId, String(camp));

  const aleCelulei = useMemo(
    () =>
      stari
        .filter((s) => s.variabile?.entryId === entryId && s.variabile.patch[camp] !== undefined)
        .map((s) => ({ status: s.status, submittedAt: s.submittedAt })),
    [stari, entryId, camp],
  );

  return derivaStareCelula(aleCelulei, editare);
}

/** Sterge marcajul `Nesalvat` de pe o celula (butonul `Renunță` de langa chip). */
export function useRenuntaLaEditare(): (entryId: number, camp: CampIntrare) => void {
  return useCallback((entryId: number, camp: CampIntrare) => {
    magazinEditari.curata('intrare', entryId, [String(camp)]);
  }, []);
}

// ---------------------------------------------------------------------------
// POST /api/zi/:data/runda
// ---------------------------------------------------------------------------

export type IntrareRundaNoua = CuImplicite<CreateRunInput, 'kind' | 'legalEntity'>;

export interface VariabileCreeazaRunda {
  data: string;
  runda: IntrareRundaNoua;
}

export function fnCreeazaRunda(variabile: VariabileCreeazaRunda): Promise<DayRunDto> {
  return api.post<DayRunDto>(`/zi/${variabile.data}/runda`, variabile.runda);
}

export function useCreeazaRunda(): UseMutationResult<DayRunDto, Error, VariabileCreeazaRunda> {
  const queryClient = useQueryClient();
  return useMutation<DayRunDto, Error, VariabileCreeazaRunda>({
    mutationKey: chei.mutatii.creeazaRunda(),
    mutationFn: fnCreeazaRunda,
    onSuccess: (_raspuns, variabile) => {
      void invalideazaDupaScriereZi(queryClient, variabile.data);
    },
  });
}

// ---------------------------------------------------------------------------
// POST /api/zi/:data/plata
// ---------------------------------------------------------------------------

export type IntrarePlataNoua = CuImplicite<CreateEntryInput, 'currency'>;

export interface VariabileAdaugaPlata {
  data: string;
  plata: IntrarePlataNoua;
}

export function fnAdaugaPlata(variabile: VariabileAdaugaPlata): Promise<RaspunsPatchIntrare> {
  return api.post<RaspunsPatchIntrare>(`/zi/${variabile.data}/plata`, variabile.plata);
}

/**
 * Adaugarea unei plati.
 *
 * **Fara scriere optimista, spre deosebire de editarea unei celule.** Diferenta e deliberata: la
 * editare stim exact ce rand se schimba si putem sa-l punem inapoi daca serverul refuza. Aici randul
 * nu exista inca — nu are id, nu are suma in lei calculata cu cursul serverului, si poate fi refuzat
 * din patru motive reale (furnizor duplicat in runda, curs lipsa, zi in afara calendarului, rol
 * insuficient). Un rand fantoma care apare si dispare dupa 300 ms e mai rau decat o asteptare.
 */
export function useAdaugaPlata(): UseMutationResult<
  RaspunsPatchIntrare,
  Error,
  VariabileAdaugaPlata
> {
  const queryClient = useQueryClient();
  return useMutation<RaspunsPatchIntrare, Error, VariabileAdaugaPlata>({
    mutationKey: chei.mutatii.adaugaPlata(),
    mutationFn: fnAdaugaPlata,
    onSuccess: (_raspuns, variabile) => {
      void invalideazaDupaScriereZi(queryClient, variabile.data);
    },
  });
}

// ---------------------------------------------------------------------------
// DELETE /api/entries/:id  &  POST /api/entries/:id/restaurare
// ---------------------------------------------------------------------------

export interface VariabileStergePlata {
  data: string;
  entryId: number;
  version: number;
  motiv: string;
}

export interface RaspunsStergePlata {
  deleted: { id: number; deletedAt: number; reason: string };
  run: { runId: number; dePlataMinor: number | null };
}

/**
 * Stergerea unei plati.
 *
 * Fara scriere optimista, si aici motivul e mai puternic decat la adaugare: o stergere optimista
 * ar face randul sa dispara de sub ochi si sa REAPARA daca serverul refuza (versiune veche, plata
 * amanata mai departe, rol insuficient). Un rand care clipeste asa e exact experienta care te face
 * sa nu mai ai incredere in ce vezi pe ecran.
 */
export function useStergePlata(): UseMutationResult<
  RaspunsStergePlata,
  Error,
  VariabileStergePlata
> {
  const queryClient = useQueryClient();
  return useMutation<RaspunsStergePlata, Error, VariabileStergePlata>({
    mutationKey: chei.mutatii.stergePlata(),
    mutationFn: (v) =>
      api.sterge<RaspunsStergePlata>(`/entries/${v.entryId}`, {
        corp: { version: v.version, reason: v.motiv },
      }),
    retry: false,
    onSuccess: (_raspuns, variabile) => {
      void invalideazaDupaScriereZi(queryClient, variabile.data);
      // Jurnalul tocmai a capatat un rand; ecranul de administrare nu are voie sa-l rateze.
      void queryClient.invalidateQueries({ queryKey: chei.jurnal.tot() });
    },
  });
}

export interface VariabileRestaureazaPlata {
  /** Ziua in care se intoarce plata, ca sa se poata invalida exact ecranul ei. */
  data: string;
  entryId: number;
  motiv?: string | null;
}

export function useRestaureazaPlata(): UseMutationResult<
  RaspunsPatchIntrare,
  Error,
  VariabileRestaureazaPlata
> {
  const queryClient = useQueryClient();
  return useMutation<RaspunsPatchIntrare, Error, VariabileRestaureazaPlata>({
    mutationKey: chei.mutatii.restaureazaPlata(),
    mutationFn: (v) =>
      api.post<RaspunsPatchIntrare>(`/entries/${v.entryId}/restaurare`, {
        reason: v.motiv ?? null,
      }),
    retry: false,
    onSuccess: (_raspuns, variabile) => {
      void invalideazaDupaScriereZi(queryClient, variabile.data);
      void queryClient.invalidateQueries({ queryKey: chei.jurnal.tot() });
    },
  });
}

// ---------------------------------------------------------------------------
// POST /api/entries/bulk
// ---------------------------------------------------------------------------

export interface RaspunsActiuniBloc {
  action: BulkEntriesInput['action'];
  updated?: number;
  runs?: Array<{ runId: number; dePlataMinor: number | null }>;
  /** Doar pentru `postpone`: rezultat PER RAND, pentru ca fiecare amanare se poate refuza singura. */
  results?: Array<{ entryId: number; ok: boolean; result?: unknown; error?: string }>;
}

export interface VariabileActiuniBloc {
  data: string;
  actiune: BulkEntriesInput;
}

export function fnActiuniBloc(variabile: VariabileActiuniBloc): Promise<RaspunsActiuniBloc> {
  return api.post<RaspunsActiuniBloc>('/entries/bulk', variabile.actiune);
}

interface ContextBloc {
  anterior: DayWorkspaceResponse | undefined;
}

export function useActiuniBloc(): UseMutationResult<
  RaspunsActiuniBloc,
  Error,
  VariabileActiuniBloc,
  ContextBloc
> {
  const queryClient = useQueryClient();

  return useMutation<RaspunsActiuniBloc, Error, VariabileActiuniBloc, ContextBloc>({
    mutationKey: chei.mutatii.actiuniBloc(),
    mutationFn: fnActiuniBloc,

    onMutate: async (variabile) => {
      await queryClient.cancelQueries({ queryKey: chei.zi.data(variabile.data) });
      const anterior = queryClient.getQueryData<DayWorkspaceResponse>(chei.zi.data(variabile.data));

      // Doar `status` se poate anticipa. `postpone` CREEAZA randuri noi in alta zi, iar `attach`
      // schimba contoare pe care clientul nu le poate calcula — pentru ele se asteapta serverul.
      if (anterior && variabile.actiune.action === 'status') {
        const selectate = new Set(variabile.actiune.entryIds);
        const status = variabile.actiune.status;
        queryClient.setQueryData<DayWorkspaceResponse>(chei.zi.data(variabile.data), {
          ...anterior,
          entries: anterior.entries.map((e) =>
            selectate.has(e.id)
              ? {
                  ...e,
                  statusOverride: status,
                  status: effectiveStatus(e.statusImported as StatusCode, status as StatusCode),
                }
              : e,
          ),
        });
      }

      return { anterior };
    },

    onError: (_eroare, variabile, context) => {
      if (context?.anterior) {
        queryClient.setQueryData(chei.zi.data(variabile.data), context.anterior);
      }
    },

    onSuccess: () => {
      // O amanare in masa muta randuri in ALTE zile: prefixul `zi` se invalideaza intreg.
      void queryClient.invalidateQueries({ queryKey: chei.zi.tot() });
      void queryClient.invalidateQueries({ queryKey: chei.matrice.tot() });
      void queryClient.invalidateQueries({ queryKey: chei.cashflow.tot() });
      void queryClient.invalidateQueries({ queryKey: chei.amanare.tot() });
    },
  });
}
